import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Box, Button, ButtonGroup, Popover, Stack, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import type { AnalyticsPeriod } from "../../../types";

export interface DateRangeValue {
  period?: AnalyticsPeriod;
  startDate?: string;
  endDate?: string;
}

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(
    value.startDate ? dayjs(value.startDate) : null,
  );
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(
    value.endDate ? dayjs(value.endDate) : null,
  );

  const handlePeriodClick = (period: AnalyticsPeriod) => {
    onChange({ period });
  };

  const handleCustomClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyCustomRange = () => {
    if (tempStartDate && tempEndDate) {
      onChange({
        startDate: tempStartDate.format("YYYY-MM-DD"),
        endDate: tempEndDate.format("YYYY-MM-DD"),
      });
      handleClose();
    }
  };

  const isCustomActive = value.startDate && value.endDate && !value.period;
  const open = Boolean(anchorEl);

  const getLabel = () => {
    if (isCustomActive) {
      return `${dayjs(value.startDate).format("MMM D")} - ${dayjs(value.endDate).format("MMM D, YYYY")}`;
    }
    return null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => handlePeriodClick("7d")}
            variant={value.period === "7d" ? "contained" : "outlined"}
          >
            7d
          </Button>
          <Button
            onClick={() => handlePeriodClick("30d")}
            variant={value.period === "30d" ? "contained" : "outlined"}
          >
            30d
          </Button>
          <Button
            onClick={() => handlePeriodClick("90d")}
            variant={value.period === "90d" ? "contained" : "outlined"}
          >
            90d
          </Button>
          <Button
            onClick={() => handlePeriodClick("all")}
            variant={value.period === "all" ? "contained" : "outlined"}
          >
            All
          </Button>
          <Button
            onClick={handleCustomClick}
            variant={isCustomActive ? "contained" : "outlined"}
            startIcon={<CalendarTodayIcon fontSize="small" />}
          >
            {getLabel() || "Custom"}
          </Button>
        </ButtonGroup>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select Date Range
            </Typography>
            <Stack spacing={2}>
              <DatePicker
                label="Start Date"
                value={tempStartDate}
                onChange={setTempStartDate}
                maxDate={tempEndDate || dayjs()}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
              <DatePicker
                label="End Date"
                value={tempEndDate}
                onChange={setTempEndDate}
                minDate={tempStartDate || undefined}
                maxDate={dayjs()}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
              <Button
                variant="contained"
                onClick={handleApplyCustomRange}
                disabled={!tempStartDate || !tempEndDate}
                fullWidth
              >
                Apply
              </Button>
            </Stack>
          </Box>
        </Popover>
      </Stack>
    </LocalizationProvider>
  );
}
